import {
	Breakpoints,
	BreakpointWrapper,
	Container,
	ContainerPadding,
	Layout,
} from '@volkswagen-onehub/components-core';
import { InteractionLayerSizeV2 } from '@volkswagen-onehub/layer-manager';
import React, { useEffect, useState } from 'react';
import { OfertaCardProvider } from '../context/ofertas-card-context';
import { OfertasFilterProvider } from '../context/ofertas-filter-context';
import { CustomBreakpoints } from '../helpers/breakpoints';
import { scrollToDisclaimerText } from '../helpers/ofertas-scroll';
import { useOfertasTracker } from '../helpers/use-ofertas-tracker';
import { useOfertasFilter } from '../hooks/use-ofertas-filter';
import { useOfertasState } from '../hooks/use-ofertas-state';
import { CarlineWithOfertas, Promocion } from '../services/ofertas-service/models/ofertas';
import { ContentLayer } from './layer/content-layer';
import { renderLayerWrapper } from './layer/layer-wrapper';
import { OfertaLayer } from './layer/oferta-layer';
import { OfertaCardsContainerDesktop } from './oferta-card/desktop/oferta-cards-container-desktop';
import { OfertaCardsContainerMobile } from './oferta-card/mobile/oferta-cards-container-mobile';
import { ModelFrame } from './oferta-model/model-frame';
import { TitleBackground } from './oferta-model/model-title-background/model-title-background';
import { DesktopSelector } from './selectors/desktop-selector';
import { MobileSelector } from './selectors/mobile-selector';
import { ModelListDesktop } from './selectors/model-list/model-list-desktop';
import {
	DesktopSelectorContainer,
	ModelFrameWrapper,
	OfertasMobileStyledContainer,
	OfertasSection,
	OfertasStickyContainer,
} from './ui/ofertas-styles';

export const Ofertas: React.FC<{
	hasOneCarline: boolean;
	isCallMeNowOpen: boolean;
}> = ({ hasOneCarline, isCallMeNowOpen }) => {
	const [selectedPromotion, setSelectedPromotion] = useState<Promocion | undefined>(undefined);

	/*Iniciamos state de control de layer */
	const [isLayerVisible, setIsLayerVisible] = useState(false);
	const [isLayerActive, setIsLayerActive] = useState<boolean>(false);
	const [interactionLayerSize, setInteractionLayerSize] = useState<InteractionLayerSizeV2>(InteractionLayerSizeV2.B);

	const {
		filteredCarlines,
		handleFilteredCarlines,
		selectedGamaFilter,
		setSelectedGamaFilter,
		selectedMotorizacionFilter,
		setSelectedMotorizacionFilter,
	} = useOfertasFilter();

	const {
		selectedOferta,
		isAutoScrollingRef,
		setSelectedOferta,
		activeCarlineIndex,
		setActiveCarlineIndex,
		ofertasRef,
		numberOfCarlines,
		carlineSelectedURLRef,
		anchorScrollOnFilter,
	} = useOfertasState(filteredCarlines);

	const { trackOfertasModelSelectClick, trackOfertasVehiclePageLoad } = useOfertasTracker();

	const handleLayerOnClick = () => setIsLayerActive(!isLayerActive);

	const handleScrollToDisclaimer = (reference: string) => scrollToDisclaimerText(reference, isAutoScrollingRef);

	const handleTrackingOnClick = (carline: CarlineWithOfertas, index: number) => {
		carlineSelectedURLRef.current = true;

		setActiveCarlineIndex(index);
		setSelectedOferta(carline.ofertas[0]);

		trackOfertasModelSelectClick(carline);
		trackOfertasVehiclePageLoad(carline);
	};

	useEffect(() => {
		const checkScreenSize = () => {
			if (window.innerWidth < 1200) {
				setInteractionLayerSize(InteractionLayerSizeV2.D);
			}

			if (window.innerWidth > 1199) {
				setInteractionLayerSize(InteractionLayerSizeV2.B);
			}
		};

		checkScreenSize();
		window.addEventListener('resize', checkScreenSize);

		return () => window.removeEventListener('resize', checkScreenSize);
	}, []);

	if (!filteredCarlines || filteredCarlines.length === 0) {
		return null;
	}

	const hasValidActiveCarline = activeCarlineIndex >= 0 && activeCarlineIndex < filteredCarlines.length;
	if (!hasValidActiveCarline) {
		return null;
	}

	const activeCarline = filteredCarlines[activeCarlineIndex];
	const visibleSelectedOferta =
		activeCarline.ofertas.find((oferta) => oferta.id === selectedOferta?.id) ?? activeCarline.ofertas[0];

	if (!visibleSelectedOferta || !activeCarline) {
		return null;
	}

	return (
		<OfertaCardProvider
			value={{
				hasOneCarline,
				isCallMeNowOpen,
				isLayerVisible,
				setIsLayerVisible,
				selectedOferta: visibleSelectedOferta,
				setSelectedOferta,
				setSelectedPromotion,
				selectedPromotion,
				handleLayerOnClick,
				handleScrollToDisclaimer,
			}}
		>
			{/* CALCULADORA DESACTIVADA, SI SE RETOMA SE DEBE FIXEAR */}
			{/* <FinanceLayer
				financeData={financeData}
				setIsFinanceLayerVisible={setIsFinanceLayerVisible}
				isFinanceLayerVisible={isFinanceLayerVisible}
				financeLayerRef={financeLayerRef}
				setIsLayerVisible={setIsLayerVisible}
			/> */}
			<OfertasFilterProvider
				value={{
					handleFilteredCarlines,
					selectedGamaFilter,
					selectedMotorizacionFilter,
					setSelectedGamaFilter,
					setSelectedMotorizacionFilter,
					setActiveCarlineIndex,
					anchorScrollOnFilter,
				}}
			>
				<OfertasSection
					className="ofertas__ofertas-section"
					numberOfCarlines={numberOfCarlines}
					hasOneCarline={hasOneCarline}
					ref={ofertasRef}
				>
					<ContentLayer
						className="ofertas__content-layer"
						isLayerVisible={isLayerVisible}
						layerSize={interactionLayerSize}
					>
						<OfertaLayer
							className="ofertas__oferta-layer"
							setIsLayerVisible={setIsLayerVisible}
							selectedOferta={visibleSelectedOferta}
							activeCarline={activeCarline}
							isCallMeNowOpen={isCallMeNowOpen}
							handleScrollToDisclaimer={handleScrollToDisclaimer}
						/>
					</ContentLayer>

					{activeCarlineIndex >= 0 && (
						<OfertasStickyContainer className="ofertas__ofertas-sticky-container" hasOneCarline={hasOneCarline}>
							<TitleBackground
								hasOneCarline={hasOneCarline}
								activeCarlineIndex={activeCarlineIndex}
								carlines={filteredCarlines}
							/>

							{/* OFERTAS VERSION TABLET Y DESKTOP */}
							<BreakpointWrapper min={Breakpoints.b960}>
								<Layout
									data-testid="ofertas__layout-ofertas-desktop"
									appearance={{
										[Breakpoints.default]: [{ name: 'a', columns: 24 }],
										[Breakpoints.b960]: hasOneCarline
											? [
													{ name: '.', columns: 0 },
													{ name: 'a', columns: 24 },
													{ name: '.', columns: 0 },
												]
											: [
													{ name: '.', columns: 1 },
													{ name: 'a', columns: 22 },
													{ name: '.', columns: 1 },
												],
									}}
								>
									{!hasOneCarline && (
										<>
											<DesktopSelectorContainer className="ofertas__desktop-selector-container">
												<DesktopSelector />
											</DesktopSelectorContainer>

											{/* LISTA DE CARLINES */}
											<ModelListDesktop
												hasOneCarline={hasOneCarline}
												carlines={filteredCarlines}
												activeCarlineIndex={activeCarlineIndex}
												handleTrackingOnClick={handleTrackingOnClick}
											/>
										</>
									)}

									{/* CARD DE LA OFERTA Y CARROUSEL DE IMAGENES */}
									<ModelFrameWrapper className="ofertas__model-frame-wrapper" hasOneCarline={hasOneCarline}>
										<ModelFrame carline={activeCarline} hasOneCarline={hasOneCarline} />
									</ModelFrameWrapper>

									{/* CARD OFERTA EN VERSION TABLET */}
									<BreakpointWrapper min={Breakpoints.b960} max={CustomBreakpoints.DESKTOP}>
										<OfertaCardsContainerDesktop
											className="ofertas__ofertas-card-container-tablet"
											carline={activeCarline}
										/>
									</BreakpointWrapper>
								</Layout>
							</BreakpointWrapper>

							{/* OFERTAS VERSION MOBILE */}
							<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
								<Container data-testid="ofertas__mobile-container" wrap="always">
									<OfertasMobileStyledContainer
										data-testid="ofertas__model-selector-container-Mobile"
										padding={ContainerPadding.grid001}
										horizontalAlign={'center'}
										stretchContent
									>
										{!hasOneCarline && (
											<MobileSelector
												carlines={filteredCarlines}
												activeCarlineIndex={activeCarlineIndex}
												setSelectedOferta={setSelectedOferta}
												carlineSelectedURLRef={carlineSelectedURLRef}
											/>
										)}
									</OfertasMobileStyledContainer>

									{activeCarlineIndex >= 0 && activeCarline && (
										<OfertaCardsContainerMobile
											className="ofertas__ofertas-card-container-mobile"
											carline={activeCarline}
										/>
									)}
								</Container>
							</BreakpointWrapper>
						</OfertasStickyContainer>
					)}
				</OfertasSection>

				{isLayerActive && selectedPromotion?.descripcion && renderLayerWrapper(handleLayerOnClick, selectedPromotion)}
			</OfertasFilterProvider>
		</OfertaCardProvider>
	);
};
